/**
 * API client for user account updates.
 */

import api from '../utils/axios';
import type {
  ChangeEmailRequest,
  ChangeEmailResponse,
  ChangePasswordRequest,
  ChangePasswordResponse,
  ChangeUsernameRequest,
  ChangeUsernameResponse,
  DeleteMyAccountRequest,
  ExternalLinkWarningPreferenceResponse,
  HomeAiSearchPreferenceResponse,
  ThemePreferencesResponse,
  UserDataExportResponse,
  UpdateExternalLinkWarningPreferenceRequest,
  UpdateHomeAiSearchPreferenceRequest,
  UpdateThemePreferencesRequest,
} from '../types/userAccount.types';

export const userAccountService = {
  /**
   * Updates the username for the authenticated user.
   */
  async changeUsername(data: ChangeUsernameRequest): Promise<ChangeUsernameResponse> {
    const response = await api.patch<ChangeUsernameResponse>('/users/me/username', data);
    return response.data;
  },

  /**
   * Updates the email address for the authenticated user.
   */
  async changeEmail(data: ChangeEmailRequest): Promise<ChangeEmailResponse> {
    const response = await api.patch<ChangeEmailResponse>('/users/me/email', data);
    return response.data;
  },

  /**
   * Updates the password for the authenticated user.
   */
  async changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    const response = await api.patch<ChangePasswordResponse>('/users/me/password', data);
    return response.data;
  },

  /**
   * Returns the theme preferences for the authenticated user.
   */
  async getThemePreferences(): Promise<ThemePreferencesResponse> {
    const response = await api.get<ThemePreferencesResponse>('/users/me/theme-preferences');
    return response.data;
  },

  /**
   * Updates the theme preferences for the authenticated user.
   */
  async updateThemePreferences(data: UpdateThemePreferencesRequest): Promise<ThemePreferencesResponse> {
    const response = await api.patch<ThemePreferencesResponse>('/users/me/theme-preferences', data);
    return response.data;
  },

  /**
   * Returns whether external-link warning dialogs are disabled for the current user.
   */
  async getExternalLinkWarningPreference(): Promise<ExternalLinkWarningPreferenceResponse> {
    const response = await api.get<ExternalLinkWarningPreferenceResponse>('/users/me/external-link-warning');
    return response.data;
  },

  /**
   * Updates whether external-link warning dialogs are disabled for the current user.
   */
  async updateExternalLinkWarningPreference(
    data: UpdateExternalLinkWarningPreferenceRequest
  ): Promise<ExternalLinkWarningPreferenceResponse> {
    const response = await api.patch<ExternalLinkWarningPreferenceResponse>('/users/me/external-link-warning', data);
    return response.data;
  },

  /**
   * Returns whether Home AI search is enabled for the current user.
   */
  async getHomeAiSearchPreference(): Promise<HomeAiSearchPreferenceResponse> {
    const response = await api.get<HomeAiSearchPreferenceResponse>('/users/me/home-ai-search-preference');
    return response.data;
  },

  /**
   * Updates whether Home AI search is enabled for the current user.
   */
  async updateHomeAiSearchPreference(
    data: UpdateHomeAiSearchPreferenceRequest
  ): Promise<HomeAiSearchPreferenceResponse> {
    const response = await api.patch<HomeAiSearchPreferenceResponse>('/users/me/home-ai-search-preference', data);
    return response.data;
  },

  /**
   * Downloads the authenticated user's personal-data export payload.
   */
  async exportMyData(): Promise<UserDataExportResponse> {
    const response = await api.get<UserDataExportResponse>('/users/me/data-export');
    return response.data;
  },

  /**
   * Deletes the authenticated user's account after password confirmation.
   */
  async deleteMyAccount(data: DeleteMyAccountRequest): Promise<void> {
    await api.delete('/users/me', { data });
  },
};
